import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { expValEquals } from '@confluence/feature-experiments';

import type { BodyProps } from '../controlled/shareAndRestrictDialog/body/Body.tsx';
import type { AccessRequestsDialogBodyProps } from '../controlled/shareAndRestrictDialog/body/accessRequests/AccessRequestsDialogBody.tsx';
import type { SpecificAccessDetailsProps } from '../controlled/shareAndRestrictDialog/body/specificAccessDetails/SpecificAccessDetails.tsx';
import type { SpecificAccessRowProps } from '../controlled/shareAndRestrictDialog/body/specificAccessDetails/specificAccessRow/SpecificAccessRow.tsx';
import type { PublicLinkDialogBodyProps } from '../controlled/shareAndRestrictDialog/body/publicLink/PublicLinkDialogBody.tsx';
import type { ShareToSlackDialogBodyProps } from '../controlled/shareAndRestrictDialog/body/shareToSlack/ShareToSlackDialogBody.tsx';
import type { SpecificAccessDialogBodyProps } from '../controlled/shareAndRestrictDialog/body/specificAccessDialog/SpecificAccessDialogBody.tsx';
import type { KnownErrors, Principal, Role } from '../controlled/commonTypes.tsx';

import type { StatesAndActions } from './Controller.tsx';
import { isUserPendingAccess } from './mutations/restrictionsTransformer.ts';
export function makeBodyProps(statesAndActions: StatesAndActions): BodyProps | undefined {
	const {
		masterState: {
			isPublicLinkDialogOpen,
			isShareToSlackDialogOpen,
			isAccessRequestsDialogOpen,
			isSpecificAccessExpanded,
			isSpecificAccessDialogOpen,
		},
		queriesState: { edition },
		derivedState: {
			specificAccessIds,
			shouldShowDeletedGroupDetails,
			natureOfCta,
			persistenceMode,
		},
	} = statesAndActions;

	const isAnotherDialogOpen =
		isPublicLinkDialogOpen || isShareToSlackDialogOpen || isAccessRequestsDialogOpen;

	const shouldShowSpecificAccessDetails =
		specificAccessIds.length === 1 ||
		(specificAccessIds.length > 1 && isSpecificAccessExpanded) ||
		shouldShowDeletedGroupDetails;

	// Specific Access dialog (experiment): rendered as a single self-contained body module.
	// The module owns tab state internally.
	if (
		isSpecificAccessDialogOpen &&
		expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
	) {
		return {
			specificAccessDetails: undefined,
			specificAccessDialogBody: makeSpecificAccessDialogBodyProps(statesAndActions),
			publicLinkDialogBody: undefined,
			shareToSlackDialogBody: undefined,
			accessRequestsDialogBody: undefined,
		};
	}

	if (!isAnotherDialogOpen) {
		return shouldShowSpecificAccessDetails
			? {
					specificAccessDetails:
						persistenceMode === 'live' &&
						natureOfCta !== 'none' &&
						expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
							? undefined
							: makeSpecificAccessDetailsProps(statesAndActions),
					publicLinkDialogBody: undefined,
					shareToSlackDialogBody: undefined,
				}
			: undefined;
	}

	return {
		specificAccessDetails: undefined,
		publicLinkDialogBody:
			isPublicLinkDialogOpen && edition !== ConfluenceEdition.FREE
				? makePublicLinkDialogBodyProps(statesAndActions)
				: undefined,
		shareToSlackDialogBody: isShareToSlackDialogOpen
			? makeShareToSlackDialogBodyProps(statesAndActions)
			: undefined,
		accessRequestsDialogBody: isAccessRequestsDialogOpen
			? makeAccessRequestsDialogBodyProps(statesAndActions)
			: undefined,
	};
}

function makePublicLinkDialogBodyProps(
	statesAndActions: StatesAndActions,
): PublicLinkDialogBodyProps {
	const {
		queriesState: {
			publicLinkPageData,
			hasInheritedRestrictions,
			contentRestrictions: { generalAccessLevel },
		},
		controllerProps: { refetchButtonData, flags },
	} = statesAndActions;
	return {
		publicLinkDisabled: publicLinkPageData?.isLoading
			? false
			: !publicLinkPageData.isPublicLinkAdminEnabled,
		publicLinkOn: publicLinkPageData?.isLoading
			? false
			: publicLinkPageData.isPublicLinkFullyEnabled,
		publicLinkStatus: publicLinkPageData?.isLoading
			? null
			: (publicLinkPageData.publicLinkStatus ?? null),
		canEditPublicLink: publicLinkPageData?.isLoading ? false : publicLinkPageData.canEditPublicLink,
		publicShareUrl: publicLinkPageData?.isLoading
			? ''
			: `${window.location.origin}${publicLinkPageData.publicLinkSharePath}`,
		isPublicLinkEditAllowedForContentType: publicLinkPageData?.isLoading
			? false
			: publicLinkPageData.isPublicLinkEditAllowedForContentType,
		isEditPublicLink: publicLinkPageData?.isLoading ? false : publicLinkPageData.isEditPublicLink,
		pageId: statesAndActions.controllerProps.contentId,
		refetchPublicLinkState: publicLinkPageData.isLoading ? () => {} : publicLinkPageData.refetch,
		refetchButtonData,
		flags,
		// Analytics
		hasInheritedRestrictions,
		generalAccessLevel,
	};
}

function makeShareToSlackDialogBodyProps(
	statesAndActions: StatesAndActions,
): ShareToSlackDialogBodyProps {
	const {
		actions: { closeShareToSlackDialog, showFlag },
	} = statesAndActions;

	return {
		onCloseSlack: closeShareToSlackDialog,
		showFlag,
	};
}

export function makeSpecificAccessDetailsProps(
	statesAndActions: StatesAndActions,
): SpecificAccessDetailsProps {
	const {
		derivedState: { specificAccessIds, shouldShowDeletedGroupDetails },
	} = statesAndActions;

	if (shouldShowDeletedGroupDetails) {
		return {
			principals: [makeSpecificAccessRowProps(statesAndActions, 'deleted-group')],
		};
	}

	const principals: (SpecificAccessRowProps | null)[] = specificAccessIds.map((id) =>
		makeSpecificAccessRowProps(statesAndActions, id),
	);

	return {
		principals,
	};
}

/**
 * Creates props for a single SpecificAccessRow component, or returns null if the principal should be filtered out.
 * Naming scheme caveat: this performs the functionality that would have been
 * covered by makePrincipalDetailsProps and makePrincipalAccessProps, if they
 * existed separately.
 */
function makeSpecificAccessRowProps(
	statesAndActions: StatesAndActions,
	principalId: string,
): SpecificAccessRowProps | null {
	const {
		derivedState: {
			principalsMap,
			isLockedOut,
			generalAccess,
			isRestrictionsEditable,
			willBeUnpublishedAfterApplying,
			shouldShowDeletedGroupDetails,
		},
		queriesState: { currentUser, hasInheritedRestrictions, edition },
		actions: {
			changeSpecificAccessRole,
			removeSpecificAccessPrincipal,
			toggleSpecificAccessExpanded,
			openSpecificAccessDialog,
		},
		masterState: { isSaving, isSpecificAccessExpanded, isSpecificAccessDialogOpen },
	} = statesAndActions;

	const isYou = principalId === currentUser.id;

	let principal: Principal | undefined;
	let role: Role | undefined;

	const mapEntry = principalsMap.get(principalId);

	// If the principal is in the map, then we know specifically what role they've
	// been assigned to
	if (mapEntry) {
		principal = mapEntry.principal;
		role = mapEntry.role;

		// If the principal is newly added or reassigned, we need to know about it for better errors.
		if (
			mapEntry.isNewOrUpdatedPrincipal &&
			principal.type === 'user' &&
			!isUserPendingAccess(principal)
		) {
			principal = {
				...principal,
				knownErrors: undefined,
			};
		}
	}
	// If they're not in the map, then they don't have specific access and their
	// access is whatever the General Access level would be. This should only
	// happen for the (You) role.
	else {
		role = {
			'open-can-edit': 'can-edit',
			'open-can-view': 'can-view',
			restricted: 'no-access',
		}[generalAccess]! as Role;

		/** Adding this dummy principal group to show that a group that holds restrictions to the content has been deleted.
		 * This only is accessible, and only happens, when the viewing user is using admin key to view the content.
		 * Please see CONFCLOUD-80781 for more details. https://jira.atlassian.com/browse/CONFCLOUD-80781
		 */
		if (shouldShowDeletedGroupDetails) {
			principal = {
				type: 'group',
				id: '',
				name: '',
				usageType: null,
			};
		}

		if (isYou) {
			principal = currentUser;
		} else {
			// Things have gone very wrong if we get to this point. We can't not
			// supply props, so we'll just show the id as their name.
			principal = {
				type: 'user',
				id: principalId,
				avatarUrl: '',
				name: principalId,
				email: '',
				knownErrors: undefined,
				sitePermissionType: '',
			};
		}
	}

	const updateKnownErrors = (id: string, errors: KnownErrors) => {
		const currentPrincipal = principalsMap.get(id);
		if (currentPrincipal && currentPrincipal.principal.type === 'user') {
			currentPrincipal.principal.knownErrors = errors;
		}
	};

	const isEditable = () => {
		if (shouldShowDeletedGroupDetails) {
			return false;
		}
		return !isYou && isRestrictionsEditable;
	};

	return {
		key: principal.id,
		isExpanded: isSpecificAccessExpanded,
		isSpecificAccessDialogOpen,
		onExpandClick: toggleSpecificAccessExpanded,
		onOpenSpecificAccessDialog: openSpecificAccessDialog,
		principalAccess: {
			isLockedOut,
			access: role,
			isEditable: isEditable(),
			isCanViewDisabled: generalAccess !== 'restricted',
			hasInheritedRestrictions,
			isUnpublishedDraft: willBeUnpublishedAfterApplying,
			edition,
			onChange: (changedRole: Role) => {
				changeSpecificAccessRole(changedRole, principalId);
			},
			onRemove: () => {
				removeSpecificAccessPrincipal(principalId);
			},
			focusRef: null,
			isSaving,
		},
		principalDetails: {
			principal,
			isYou,
			role,
			updateKnownErrors,
			isDraft: willBeUnpublishedAfterApplying,
			shouldShowDeletedGroupDetails,
		},
	};
}

export function makeAccessRequestsDialogBodyProps(
	statesAndActions: StatesAndActions,
): AccessRequestsDialogBodyProps {
	const {
		masterState: {
			isAccessRequestsWarnRestrictionsDialogOpen,
			isAccessRequestsResolveRestrictionsDialogOpen,
		},
		controllerProps: { initialDialog },
		actions: { maybeStartBlockedAccessFlowForUser, setAccessRequestsChanges },
	} = statesAndActions;

	return {
		isInitialDialog: initialDialog === 'accessRequests',
		isBlockedAccessFlowActive:
			isAccessRequestsWarnRestrictionsDialogOpen || isAccessRequestsResolveRestrictionsDialogOpen,
		maybeStartBlockedAccessFlowForUser,
		onAccessRequestsChange: setAccessRequestsChanges,
	};
}

export function makeSpecificAccessDialogBodyProps(
	statesAndActions: StatesAndActions,
): SpecificAccessDialogBodyProps {
	const {
		derivedState: { principalCounts, persistenceMode },
		queriesState: { currentUser, principalsPendingAccess },
		controllerProps: { contentId },
		actions: { removeAllSpecificAccessPrincipals },
	} = statesAndActions;

	return {
		contentId,
		principalCounts,

		specificAccessDetails: makeSpecificAccessDetailsProps(statesAndActions),
		accessRequestsDialogBody: makeAccessRequestsDialogBodyProps(statesAndActions),
		persistenceMode,
		liveMutationContext: {
			currentUser,
			principalsPendingAccess,
		},
		stagedActionContext:
			persistenceMode === 'staged'
				? {
						removeAllSpecificAccessPrincipals,
					}
				: undefined,
	};
}
